import type Phaser from "phaser";
import { DripView } from "./drip-view.ts";
import { PuddleFeet, type FeetMode, type FigureVisit } from "./puddle-feet.ts";
import { PuddleView, type PuddleFrame } from "./puddle-view.ts";
import { EffectView, NO_STAMPS, PIXEL_TEXTURE, type CullArea, type EaseStamp, type EffectsDetail, type EffectViewStats } from "daedalus-renderer-phaser";
import { RAIN_PRESETS, STORM_WIND, WET_MAX, rainLookInto, NO_OVERRIDES, type CloudField, type MutableRainLook, type Puddle, type PuddleOverrides, type RainSlant, type WetState, type Wind } from "odyssey-shared";

import { RainStrips } from "./rain-strips.ts";
import { SoakFilter } from "./soak-filter.ts";
import { WeatherGround } from "./weather-ground.ts";
import { WindView, type WindFrame, type WindShown, type WorldRect } from "./wind-view.ts";
import type { WindSheets } from "./wind-sheets.ts";

/**
 * Rain, the sky's dim and lightning on the page (ADR-144 decision A4). The view reads one number, how much weather is
 * falling (0 clear, 1 rain, 2 storm), and draws it: strips of pooled screen-wide rain emitters from the effects library
 * (ADR-141), the dim handed to the light layer, and in a storm a white flash. Reduced motion and "Effects: Off" draw no particles
 * and no flash and keep the dim; "Effects: Reduced" halves the rain. Indoors it draws nothing. A frame reads two numbers
 * from the camera and moves the active strips; strips are attached or detached only when the strength crosses a strip count,
 * and the flash is one reused image.
 *
 * Wind (ADR-144's amendment of 2026-10-31) draws here too when the options carry it: wisps, gust dashes and cloud shadows
 * (`WindView`), and in a storm the rain leans with the wind. "Effects: Reduced" halves their counts; Off, reduced motion and
 * indoors draw none.
 *
 * At a seam crossing the scene keeps the view and stands it on the new region (`stand`): what is drawn eases from the state
 * shown to the new region's over the fade it is given (ADR-147 note 15), and the rain already falling keeps its emitters.
 *
 * The dim is the number `dim`, which the scene writes to the light layer each frame (`gameReport.lighting.weatherDim`); the
 * layer holds the darker of the sky and the weather (`min(skyAmbient, 1 - dim)`), so rain at night adds no darkness.
 */

/**
 * Where weather draws among the world's objects: above everything that stands, the silhouettes and the debug view, in this
 * order from the bottom: rain, flash.
 */
export const RAIN_DEPTH = 950_000;
export const FLASH_DEPTH = 960_000;

/**
 * The flash's colour.
 */
export const FLASH_COLOUR = 0xf2f6ff;

/**
 * How far past the camera's view the flash reaches on every side, in world pixels: the view they are laid on is the
 * last drawn frame's, so a moving camera must not show a bare edge.
 */
export const VIEW_PAD = 24;

/**
 * The rain strength from which rain hides the drip under a Drenched character standing in it.
 */
export const DRIP_HIDDEN_IN_RAIN = 0.5;

const NO_WIND_SHOWN: WindShown = { wisps: 0, dashes: 0, clouds: 0 };
const VISIBLE_ABOVE = 0.002;

/**
 * What the wind needs beyond the weather: the art, the wind and cloud field of the clock, and the seed and region the schedule is
 * drawn from.
 */
export interface WindOptions {
  readonly sheets: WindSheets;
  readonly wind: (epochMs: number) => Wind;
  readonly clouds: (epochMs: number, reduced: boolean) => CloudField;
  readonly seed: number;
  readonly region: number;
}

/**
 * What the view reads and the page decides. `fadeNow` is the monotonic clock a crossing's fade runs on (the page's
 * `performance.now` unless given), apart from the shared clock, which a crossing's `welcome` may set forward or back.
 */
export interface WeatherViewOptions {
  readonly scene: Phaser.Scene;
  readonly camera: { readonly worldView: { readonly x: number; readonly y: number; readonly width: number; readonly height: number } };
  readonly strength: (epochMs: number) => number;
  readonly flash: (epochMs: number, strength: number) => number;
  readonly now: () => number | null;
  readonly reducedMotion: () => boolean;
  readonly effects: () => EffectsDetail;
  readonly indoor: () => boolean;
  /** Whether the character is sheltered by a roof, and the place its room covers on the screen: while it is, the rain and the wind are not drawn there and everywhere else they are as outdoors (ADR-144's note of 2026-11-01). The light layer spares part of the dim and the night on the same place. Absent is never. */
  readonly roof?: { readonly inside: () => boolean; readonly area: CullArea; /** The art stamps of the place, which the soak is cut to; absent or empty leaves the soak on the whole view. */ readonly stamps?: () => readonly EaseStamp[] };
  readonly wetStart?: (epochMs: number) => WetState;
  readonly wetFrozen?: boolean;
  readonly puddles?: { readonly list: readonly Puddle[]; readonly depth: number; /** Where no puddle may lie (under a roof that opens): asked each frame until `ready` says the answer is final, when the puddles it names are dropped. */ readonly buried?: { readonly ready: () => boolean; readonly at: (puddle: Puddle) => boolean } };
  readonly feet?: { readonly mode: FeetMode; readonly figures: (visit: FigureVisit) => void };
  readonly wind?: WindOptions;
  readonly fadeNow?: () => number;
  readonly drip?: { readonly level: (id: number) => number; readonly roofed?: (x: number, y: number) => boolean; readonly blockedAt?: (x: number, y: number) => boolean };
}

/**
 * What a new region gives the view at a crossing: its weather, its ground, its puddles and its wind.
 */
export type WeatherStand = Pick<WeatherViewOptions, "strength" | "flash" | "wetStart" | "wetFrozen" | "puddles" | "wind" | "drip" | "roof">;

/**
 * The page's weather.
 */
export class WeatherView {
  private options: WeatherViewOptions;
  private readonly effects: EffectView;
  private readonly strips: RainStrips;
  private readonly look: MutableRainLook = { lightStrips: 0, heavyStrips: 0, dim: 0 };
  private puddleView: PuddleView | null = null;
  private feetView: PuddleFeet | null = null;
  private dripView: DripView | null = null;
  private readonly flashImage: Phaser.GameObjects.Image;
  private readonly soak: SoakFilter;
  private readonly ground = new WeatherGround(() => this.options, () => this.options.fadeNow?.() ?? performance.now());
  private overrides: PuddleOverrides = NO_OVERRIDES;
  private readonly puddleFrame: { -readonly [K in keyof PuddleFrame]: PuddleFrame[K] };
  private windView: WindView | null;
  private windFrame: WindFrame | null;
  private rainNow = 0;
  private buriedDone = false;
  /**
   * Whether rain falls on a world point now: it rains here (not indoors) and no roof covers the point, so the drip under a
   * Drenched character there is hidden in the rain.
   *
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns True in the rain.
   */
  private readonly rainingOn = (x: number, y: number): boolean => this.rainNow >= DRIP_HIDDEN_IN_RAIN && this.options.drip?.roofed?.(x, y) !== true;

  /**
   * @param options What the view reads.
   */
  public constructor(options: WeatherViewOptions) {
    this.options = options;
    this.soak = new SoakFilter(options.scene);
    this.effects = new EffectView({ scene: options.scene, presets: (id) => RAIN_PRESETS[id], detail: "full", depths: { behind: RAIN_DEPTH, front: RAIN_DEPTH, emissive: RAIN_DEPTH } });
    this.strips = new RainStrips(this.effects);
    this.puddleFrame = { view: options.camera.worldView, ground: this.ground.state, overrides: NO_OVERRIDES, rain: 0, detail: "full", now: 0, dry: false, from: null, mix: 1 };
    this.layPuddles(options.puddles);
    this.layDrip();
    this.flashImage = options.scene.add.image(0, 0, PIXEL_TEXTURE).setOrigin(0, 0).setTint(FLASH_COLOUR).setDepth(FLASH_DEPTH).setVisible(false);
    this.windView = options.wind === undefined ? null : new WindView(options.scene, options.wind.sheets);
    this.windFrame = options.wind === undefined ? null : { view: options.camera.worldView, epochMs: 0, wind: { strength: 0, heading: "right" }, clouds: { turn: 0, count: 0, speedPxPerS: 0, heading: "right" }, reduced: false, seed: options.wind.seed, region: options.wind.region, cover: null };
  }

  /**
   * What the wind drew in the last frame; reading it allocates nothing.
   *
   * @returns The counts of wisps, dashes and cloud shadows.
   */
  public get windShown(): WindShown {
    return this.windView?.shown ?? NO_WIND_SHOWN;
  }

  /**
   * The strength drawn in the last frame outdoors, 0 clear to 2 storm: the region's, or the eased one during a crossing's fade.
   *
   * @returns The strength.
   */
  public get strength(): number {
    return this.ground.strength;
  }

  /**
   * The puddle water drawn in the last frame (painted area times opacity), for the capture.
   *
   * @returns The sum in pixels.
   */
  public get puddleWater(): number {
    return this.puddleView?.water ?? 0;
  }

  /**
   * Whether a crossing's fade is running.
   *
   * @returns True while the drawn weather eases to the new region's.
   */
  public get fading(): boolean {
    return this.ground.fading;
  }

  /**
   * The dim now, 0 to 1, which the light layer takes with the sky's (the darker of the two holds).
   *
   * @returns The dim.
   */
  public get dim(): number {
    return this.look.dim;
  }

  /**
   * How wet the ground is, 0 dry to 1 as wet as rain makes it: the wet minutes (`odyssey-shared/src/weather/wetness.ts`) over
   * the most the ground holds.
   *
   * @returns The wetness.
   */
  public get wetness(): number {
    return this.ground.state.large / WET_MAX;
  }

  /**
   * Sets what the server says happened to puddles that the weather does not know (drained, taken from); the view draws each
   * puddle at the amount the overrides leave. Nothing sends overrides yet.
   *
   * @param overrides The overrides by puddle key.
   */
  public setOverrides(overrides: PuddleOverrides): void {
    this.overrides = overrides;
  }

  /**
   * How many puddles are on screen, for the capture.
   *
   * @returns The count.
   */
  public get puddlesShown(): number {
    return this.puddleView?.shown ?? 0;
  }

  /**
   * How many drip puddles hold water, for the capture.
   *
   * @returns The count.
   */
  public get dripPuddles(): number {
    return this.dripView?.puddlesShown ?? 0;
  }

  /**
   * How many drip drops fall or ring now, for the browser suite.
   *
   * @returns The count.
   */
  public get dripDrops(): number {
    return this.dripView?.dropsFalling ?? 0;
  }

  /**
   * The soak on the camera, which reads whether one is laid.
   *
   * @returns The soak's filter.
   */
  public get soakFilter(): { readonly active: boolean } {
    return this.soak;
  }

  /**
   * The wet minutes now, in real minutes of steady rain: the number the soak and the puddles are read from.
   *
   * @returns The wet minutes.
   */
  public get wet(): number {
    return this.ground.state.large;
  }

  /**
   * How many rain emitters run now; reading it allocates nothing.
   *
   * @returns The count.
   */
  public get emitters(): number {
    return this.effects.activeCount;
  }

  /**
   * The pool budget of the running rain emitters; reading it allocates nothing.
   *
   * @returns The budget in particles.
   */
  public get budget(): number {
    return this.effects.budgetInUse;
  }

  /**
   * What the effects view holds, for the capture and the bench.
   *
   * @returns The effects view's counts.
   */
  public get stats(): EffectViewStats {
    return this.effects.stats;
  }

  /**
   * Whether a puddle of the map's list is still drawn, that is, none dropped it as lying where something stands on the water.
   *
   * @param puddle The puddle.
   * @returns True when it is.
   */
  public holdsPuddle(puddle: Puddle): boolean {
    return this.puddleView?.holds(puddle) !== false;
  }

  /**
   * Drops the puddles that lie where something stands on the water, once for each map, when the answer is final.
   */
  private dropBuried(): void {
    const buried = this.options.puddles?.buried;
    if (this.buriedDone || buried === undefined || this.puddleView === null || !buried.ready()) return;
    this.buriedDone = true;
    this.puddleView.drop(buried.at);
  }

  /**
   * Pauses or resumes the rain with the shared clock.
   *
   * @param paused Whether the weather stands still.
   */
  public setPaused(paused: boolean): void {
    this.effects.setPaused(paused);
  }

  /**
   * Stands the view on a new region at a seam crossing: the region's weather, ground, puddles and wind replace the old, and
   * what is drawn eases from the state shown now to the new region's over `fadeMs`. The rain's emitters are kept, so rain
   * falling on both sides attaches nothing. A fade of 0, or a page that does not know the shared time, switches at once.
   *
   * @param next The new region's feed, puddles and wind.
   * @param fadeMs How long the fade runs, in milliseconds.
   */
  public stand(next: WeatherStand, fadeMs: number): void {
    const old = this.options;
    this.options = { scene: old.scene, camera: old.camera, now: old.now, reducedMotion: old.reducedMotion, effects: old.effects, indoor: old.indoor, ...(old.feet === undefined ? {} : { feet: old.feet }), ...(old.fadeNow === undefined ? {} : { fadeNow: old.fadeNow }), strength: next.strength, flash: next.flash, ...(next.wetStart === undefined ? {} : { wetStart: next.wetStart }), ...(next.wetFrozen === undefined ? {} : { wetFrozen: next.wetFrozen }), ...(next.puddles === undefined ? {} : { puddles: next.puddles }), ...(next.wind === undefined ? {} : { wind: next.wind }), ...(next.drip === undefined ? {} : { drip: next.drip }), ...(next.roof === undefined ? {} : { roof: next.roof }) };
    this.soak.release();
    this.layPuddles(next.puddles);
    this.layWind(next.wind);
    this.layDrip();
    this.ground.stand(this.options.now(), fadeMs);
  }

  /**
   * Draws the frame: reads the weather, keeps the strips the strength calls for, moves them with the camera, and sets the dim
   * (for the light layer) and the flash.
   */
  public update(): void {
    const options = this.options;
    this.dropBuried();
    const reduced = options.reducedMotion();
    const detail: EffectsDetail = reduced ? "off" : options.effects();
    this.effects.setDetail(detail);
    const clock = options.now();
    const now = clock ?? 0;
    const indoor = options.indoor();
    const roofed = !indoor && options.roof?.inside() === true;
    const cover = roofed ? (options.roof as NonNullable<typeof options.roof>).area : null;
    this.effects.setCull(cover);
    const outdoors = this.ground.step(clock, clock === null ? 0 : options.strength(now));
    this.soak.lay(indoor ? 0 : this.ground.state.soak, roofed ? (options.roof?.stamps?.() ?? NO_STAMPS) : NO_STAMPS);
    const strength = indoor ? 0 : outdoors;
    this.rainNow = strength;
    const look = rainLookInto(strength, this.look);
    const view = options.camera.worldView;
    const wind = options.wind === undefined || indoor || clock === null ? null : options.wind.wind(clock);
    const slant: RainSlant = wind !== null && wind.strength === STORM_WIND ? wind.heading : "none";
    this.drawWind(wind, detail, clock, view, cover);
    this.strips.draw(look, view, slant);
    const puddleFrame = this.puddleFrame;
    puddleFrame.view = view;
    puddleFrame.overrides = this.overrides;
    puddleFrame.rain = strength;
    puddleFrame.detail = detail;
    puddleFrame.now = now;
    puddleFrame.dry = indoor;
    this.ground.puddlesInto(puddleFrame);
    this.puddleView?.update(puddleFrame);
    if (!indoor) this.feetView?.update(now, detail);
    this.dripView?.update(now, detail);
    const flash = detail === "off" || indoor || clock === null ? 0 : options.flash(now, strength);
    this.lay(this.flashImage, flash, view);
  }

  /**
   * Draws the wind, or hides it when there is none to draw.
   *
   * @param wind The wind of the turn, or null indoors or without wind art.
   * @param detail The effects detail.
   * @param now The clock.
   * @param view The camera's world view.
   * @param cover The place where none is drawn, or null.
   */
  private drawWind(wind: Wind | null, detail: EffectsDetail, now: number | null, view: WorldRect, cover: CullArea | null): void {
    const windView = this.windView;
    const frame = this.windFrame;
    const options = this.options.wind;
    if (windView === null || frame === null || options === undefined) return;
    if (wind === null || now === null || detail === "off") {
      windView.clear();
      return;
    }
    const reduced = detail === "reduced";
    frame.view = view;
    frame.epochMs = now;
    frame.wind = wind;
    frame.clouds = options.clouds(now, reduced);
    frame.reduced = reduced;
    frame.cover = cover;
    windView.update(frame);
  }

  /**
   * Gives every emitter back and destroys the images.
   */
  public destroy(): void {
    this.soak.destroy();
    this.windView?.destroy();
    this.effects.dispose();
    this.feetView?.destroy();
    this.dripView?.destroy();
    this.puddleView?.destroy();
    this.flashImage.destroy();
  }

  /**
   * Makes the puddle view for a list, or gives an existing one the new list, keeping its pooled images.
   *
   * @param puddles The puddles and the depth they draw at, or undefined for none.
   */
  private layPuddles(puddles: WeatherViewOptions["puddles"]): void {
    this.buriedDone = false;
    if (puddles === undefined) return;
    if (this.puddleView !== null) {
      this.puddleView.reset(puddles.list, puddles.depth);
      return;
    }
    const options = this.options;
    this.puddleView = new PuddleView({ scene: options.scene, puddles: puddles.list, depth: puddles.depth });
    this.feetView = options.feet === undefined ? null : new PuddleFeet({ scene: options.scene, puddles: this.puddleView, mode: options.feet.mode, figures: options.feet.figures });
  }

  /**
   * Makes the drip view for the map stood on: its depth, its blocked cells and its roofs are the map's, so a crossing makes
   * it afresh, and the old map's drip puddles, placed in the old frame, go with the old map.
   */
  private layDrip(): void {
    this.dripView?.destroy();
    const options = this.options;
    const drip = options.drip;
    this.dripView = drip === undefined || options.feet === undefined || options.puddles === undefined ? null : new DripView({ scene: options.scene, groundDepth: options.puddles.depth, figures: options.feet.figures, level: drip.level, rainingOn: this.rainingOn, ...(drip.blockedAt === undefined ? {} : { blockedAt: drip.blockedAt }) });
  }

  /**
   * Points the wind at a new region's schedule, making the wind view when the first wind arrives; a region without wind art
   * takes the wind view away, so the old region's wind is never drawn on it.
   *
   * @param wind The wind, or undefined for none.
   */
  private layWind(wind: WindOptions | undefined): void {
    if (wind === undefined) {
      this.windView?.destroy();
      this.windView = null;
      this.windFrame = null;
      return;
    }
    this.windView ??= new WindView(this.options.scene, wind.sheets);
    if (this.windFrame === null) {
      this.windFrame = { view: this.options.camera.worldView, epochMs: 0, wind: { strength: 0, heading: "right" }, clouds: { turn: 0, count: 0, speedPxPerS: 0, heading: "right" }, reduced: false, seed: wind.seed, region: wind.region, cover: null };
      return;
    }
    this.windFrame.seed = wind.seed;
    this.windFrame.region = wind.region;
  }

  /**
   * Lays a full-view image at an alpha; an alpha of nothing hides it.
   *
   * @param image The image.
   * @param alpha Its alpha.
   * @param view The camera's world view.
   */
  private lay(image: Phaser.GameObjects.Image, alpha: number, view: { readonly x: number; readonly y: number; readonly width: number; readonly height: number }): void {
    if (alpha <= VISIBLE_ABOVE) {
      if (image.visible) image.setVisible(false);
      return;
    }
    image.setVisible(true).setAlpha(alpha).setPosition(view.x - VIEW_PAD, view.y - VIEW_PAD).setDisplaySize(view.width + 2 * VIEW_PAD, view.height + 2 * VIEW_PAD);
  }
}
